import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { facilitiesLegendItemsAtom } from "atoms/map/payload/facilities";
import { wellsLegendItemsAtom } from "atoms/map/payload/wells";
import { isForecastTrumpingSource } from "constants/settings.constants";
import { useAtomValue } from "jotai";
import { RootState } from "store/rootReducer";
import {
  selectOthersMembersUnavailable,
  useOthersMembersAvailabilityStore
} from "stores/othersMembersAvailabilityStore";
import styled from "styled-components";
import { predicateSourceMigration } from "utils/filter";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import useDebounce from "hooks/useDebounce";
import { useMaxBinDefaults } from "hooks/useMaxBinDefaults";

import {
  BIN_SEARCH_DEBOUNCE_MS,
  isSearchWithinLimit,
  toGroupByPdenSource,
  toBinSearchBin
} from "api/othersMembers";
import { useOthersMembersQuery } from "api/useOthersMembersQuery";

import { LegendItemModel } from "models/LegendItem";
import { EntityKind } from "models/entityKind";

import { BaseCheckboxInput } from "components/base";
import { LegendExtraRow, LegendOthersExpansion } from "components/ui/Legend";

/** MaxBins can arrive as a string when BinSettings edits a non-Integer (Text) focus. */
function toPositiveFiniteMaxBins(v: Maybe<number | string>): Nullable<number> {
  if (v === "" || v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

function useOthersMembersGate(entityKind: EntityKind) {
  const maxBinDefaultsEnabled = useMaxBinDefaults();
  const isWell = entityKind === EntityKind.Well;

  const filterId = useSelector((state: RootState) =>
    isWell ? state.filter.filterId : state.facilityFilter.filterId
  );
  const groupBy = useSelector((state: RootState) =>
    isWell ? state.groupBy.globalGroupBy : state.groupBy.globalFacilityFocus
  );
  const showGroupsNotInFilter = useSelector((state: RootState) =>
    isWell
      ? state.filter.showGroupsNotInFilter
      : state.facilityFilter.showGroupsNotInFilter
  );
  const sortBy = useSelector((state: RootState) =>
    isWell ? state.filter.sortBy : state.facilityFilter.sortBy
  );
  const normalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );

  const legendItems = useAtomValue(
    isWell ? wellsLegendItemsAtom : facilitiesLegendItemsAtom
  );
  const hasOthersRow = legendItems.some((item) => isOthersGroupLabel(item.title));

  const isGroupMode = !!groupBy.mode;
  const isForecastTrumping = isForecastTrumpingSource(groupBy.forecastFolder);
  const hasPerformanceDateRange = !!groupBy.performanceDateRange;

  const maxBins = toPositiveFiniteMaxBins(groupBy.bin?.MaxBins);
  const enabled =
    maxBinDefaultsEnabled === true &&
    maxBins !== null &&
    hasOthersRow &&
    !isGroupMode &&
    !isForecastTrumping &&
    !hasPerformanceDateRange;

  const request = {
    filterId: filterId.id,
    property: groupBy.property,
    bin: toBinSearchBin(groupBy.bin),
    entityKind,
    pdenSource: toGroupByPdenSource(groupBy.pdenSource),
    normalizeBySetting: useNormalizeBy ? normalizeBy : null,
    showGroupsNotInFilter,
    sortBy
  };

  return { enabled, request };
}

// Where the Others row goes among the visible rows while searching.
type OthersRow = "asFiltered" | "hidden" | "shown";

// On AWS the Others row is always drawn last; the group-by can sort it in
// among the bins (e.g. after a regroup), which reads as a real value.
function withOthersRow(
  row: OthersRow,
  legendItems: LegendItemModel[],
  filteredItems: LegendItemModel[],
  othersLast: boolean
): LegendItemModel[] {
  if (row === "asFiltered" && !othersLast) return filteredItems;
  const rest = filteredItems.filter((item) => !isOthersGroupLabel(item.title));
  if (row === "hidden") return rest;
  const others =
    filteredItems.find((item) => isOthersGroupLabel(item.title)) ??
    (row === "shown"
      ? legendItems.find((item) => isOthersGroupLabel(item.title))
      : undefined);
  return others ? [...rest, others] : rest;
}

export interface OthersMembersLegend {
  items: LegendItemModel[];
  // Null (plain checkbox) where Others members aren't available.
  othersExpansion: Nullable<LegendOthersExpansion>;
  isSearchPending: boolean;
}

/**
 * The focus legend's rows and Others-row expansion. While searching, the
 * Others row stands for the members that match. Nothing is fetched until
 * the row is expanded or a search is typed.
 */
export function useOthersMembersExpansion(
  entityKind: EntityKind,
  search: string,
  legendItems: LegendItemModel[],
  filteredItems: LegendItemModel[],
  selectMember?: (member: { title: string; count: number }) => void
): OthersMembersLegend {
  const maxBinDefaultsEnabled = useMaxBinDefaults() === true;
  const gate = useOthersMembersGate(entityKind);
  const isUnavailable = useOthersMembersAvailabilityStore(selectOthersMembersUnavailable);
  const isAvailable = gate.enabled && !isUnavailable;

  const debouncedSearch = useDebounce(search, BIN_SEARCH_DEBOUNCE_MS);
  const isSearching = search.trim() !== "";
  // Checked on the untrimmed value, since that is what is sent.
  const canSearch =
    isSearching && debouncedSearch.trim() !== "" && isSearchWithinLimit(debouncedSearch);

  // Open only for the request and search it was opened on, so it never
  // survives a filter/focus/bin/search change. A sort change reorders the
  // open list instead.
  // MaxBins is left out: a pick that raises it (lockBinsToFit) changes it ahead
  // of its filterId, and the query, keyed on the full bin, refetches anyway.
  const requestKey = JSON.stringify({
    ...gate.request,
    bin: gate.request.bin && { ...gate.request.bin, MaxBins: undefined },
    sortBy: undefined,
    search
  });
  // A check re-posts the filter, so its new filterId changes requestKey;
  // `carry` counts the ticks whose filterId has not landed yet, so overlapping
  // ticks keep the list open instead of closing it after the first change. A
  // coalesced post leaves a spare count, which at worst keeps the list open once
  // more. Held in state, not a ref: StrictMode renders twice, and a ref consumed
  // during render is already spent on the second pass.
  const [open, setOpen] = useState<Nullable<{ key: string; carry: number }>>(null);
  // A search opens the list until the user closes it; clearing the search resets that.
  const [isClosedForSearch, setIsClosedForSearch] = useState(false);
  if (!isSearching && isClosedForSearch) setIsClosedForSearch(false);
  if (open && open.key !== requestKey)
    setOpen(open.carry > 0 ? { key: requestKey, carry: open.carry - 1 } : null);

  // The checkbox is Well-only, so Facility lists have no checked rows to hide.
  const canCheck = entityKind === EntityKind.Well;
  const propertiesFilter = useSelector((state: RootState) =>
    canCheck ? state.filter.propertiesFilter : undefined
  );
  const checkedTitles = useMemo(() => {
    const titles = new Set<string>();
    for (const filter of propertiesFilter ?? []) {
      for (const predicate of filter.predicates) {
        if (predicate.property !== gate.request.property || predicate.rollup) continue;
        titles.add(predicate.sourceKey || predicateSourceMigration(predicate));
      }
    }
    return titles;
  }, [propertiesFilter, gate.request.property]);

  // Asks only while the search is settled: typing on moves the query off the old
  // term, and react-query aborts its request once nothing observes it.
  const isSettled = search === debouncedSearch;
  const searchQuery = useOthersMembersQuery({
    view: "others",
    enabled: isAvailable && canSearch && isSettled,
    request: gate.request,
    search: isSettled ? debouncedSearch : ""
  });
  // A failed search has no matches to show, so it leaves the list closed.
  const expanded =
    open?.key === requestKey ||
    (isSearching &&
      isSearchWithinLimit(search) &&
      !isClosedForSearch &&
      !searchQuery.isError);
  // A failed search keeps the unfiltered list behind the original row. The list
  // also leaves an unsettled term, so nothing keeps its request alive.
  const listSearch =
    canSearch && !searchQuery.isError && isSettled ? debouncedSearch : "";
  const query = useOthersMembersQuery({
    view: "others",
    enabled: isAvailable && expanded && (!isSearching || isSettled),
    request: gate.request,
    search: listSearch
  });

  let othersRow: OthersRow = "asFiltered";
  let label: LegendOthersExpansion["label"] = null;
  // Waiting out the debounce or the first answer; a term over the limit never asks.
  const isSearchPending =
    isAvailable &&
    isSearching &&
    isSearchWithinLimit(search) &&
    (!isSettled || searchQuery.isLoading);
  if (isSearchPending) {
    // The row stays in place while the search runs instead of dropping out and back.
    othersRow = "shown";
    label = { text: "Searching Others…", count: null };
  } else if (isAvailable && isSearching) {
    if (canSearch && searchQuery.isError) {
      othersRow = "shown";
    } else if (canSearch && searchQuery.total > 0) {
      othersRow = "shown";
      // Only a fully loaded match list sums to the right well count.
      const allLoaded = searchQuery.members.length === searchQuery.total;
      label = {
        text: `${searchQuery.total} Others`,
        count: allLoaded
          ? searchQuery.members.reduce((sum, member) => sum + member.count, 0)
          : null
      };
    } else {
      othersRow = "hidden";
    }
  }

  const items = useMemo(
    () => withOthersRow(othersRow, legendItems, filteredItems, maxBinDefaultsEnabled),
    [othersRow, legendItems, filteredItems, maxBinDefaultsEnabled]
  );

  if (!isAvailable) return { items, othersExpansion: null, isSearchPending };
  const onToggle = () => {
    setOpen(expanded ? null : { key: requestKey, carry: 0 });
    setIsClosedForSearch(expanded && isSearching);
  };
  // A disabled query still holds its cached pages.
  if (!expanded) {
    return {
      items,
      othersExpansion: { expanded, label, loading: false, onToggle, rows: [] },
      isSearchPending
    };
  }

  const onCheck = (member: { title: string; count: number }) => {
    setOpen({ key: requestKey, carry: (open?.carry ?? 0) + 1 });
    selectMember?.(member);
  };

  // A checked value leaves the list at once, before the re-posted filter's rows land.
  // While the term is still being typed no members show, so none from an older term linger.
  const isTyping = isSearching && !isSettled;
  const rows: LegendExtraRow[] = (isTyping ? [] : query.members)
    .filter((member) => !checkedTitles.has(member.title))
    .map((member) => ({
      key: `others-member-${member.title}`,
      content: (
        <MemberRow data-testid="others-member-row">
          {canCheck && (
            // BaseCheckboxInput keeps its own checked state; the key stops a
            // position-reusing list from carrying a tick over to the next member.
            <BaseCheckboxInput
              key={member.title}
              aria-label={`Select ${member.title}`}
              value={false}
              onChange={() => onCheck(member)}
            />
          )}
          <MemberTitle title={member.title}>{member.title}</MemberTitle>
          <MemberCount>{member.count}</MemberCount>
        </MemberRow>
      )
    }));
  if (isTyping || query.isLoading || query.isFetchingNextPage) {
    rows.push({
      key: "others-members-loading",
      content: <MemberRow data-testid="others-members-loading">Loading…</MemberRow>
    });
  } else if (query.isError) {
    rows.push({
      key: "others-members-error",
      content: (
        <MemberRow data-testid="others-members-error">
          Couldn&apos;t load Others values.
          <LinkButton type="button" onClick={query.refetch}>
            Retry
          </LinkButton>
        </MemberRow>
      )
    });
  } else if (query.hasNextPage) {
    rows.push({
      key: "others-members-show-more",
      content: (
        <MemberRow>
          <LinkButton
            type="button"
            data-testid="others-members-show-more"
            onClick={query.fetchNextPage}>
            Show more ({Math.max(query.total - query.members.length, 0)} items)
          </LinkButton>
        </MemberRow>
      )
    });
  }

  return {
    items,
    othersExpansion: { expanded, label, loading: query.isLoading, onToggle, rows },
    isSearchPending
  };
}

// Indented under the Others row's chevron, with a tree-guide line.
const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: calc(12px + var(--space-1) + 1rem);
  padding: 3px 12px;
  border-left: 1px solid var(--color-text-20);
  color: var(--color-text-60);
  font-size: 1.3rem;
`;

const MemberTitle = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Badge's padding, so member counts line up with the legend's counts.
const MemberCount = styled.span`
  padding-right: 6px;
  font-family: var(--fontMono);
`;

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  cursor: pointer;
  font-size: inherit;
`;
